// ─────────────────────────────────────────────
//  CaseBarShell — THE blue box: one bar per case, and everything that is true
//  of it whoever is standing in the room.
//
//  ⚠️ ONE BOX. An earlier attempt drew a paired room as two detached bars and
//  the owner rejected it on sight: *"the 2 CRNA need to be inside the blue
//  box."* A case is one thing. Who is in it is bands INSIDE this box (see
//  `CaseBar`), never a second box beside it.
//
//  ⚠️ THE STRUCTURE IS THE ACCESSIBILITY. Siblings, never nested:
//
//      <div>                          the bar itself — PRESENTATION ONLY
//        <div role="button">          OPEN THE CASE — absolute, fills the bar
//        …bands, each with a <button>NAME</button>…   OPEN THE MENU, above it
//        <span role="slider"> …       the drag handles
//
//  The name used to be a text span inside a bar that was itself the button.
//  Making it a real control there would have nested a button inside a button:
//  invalid HTML, and in practice a control neither a keyboard nor a screen
//  reader can reach. So the bar's `role="button"` moved OFF the container and
//  onto a transparent overlay, and the names became its siblings. The overlay
//  still covers every pixel a name does not, which is what keeps "click the
//  rest of the bar → the case modal" true.
//
//  ⚠️ THE OVERLAY CARRIES THE SPOKEN SENTENCE. The visible text is what
//  survived the bar's width and is all `aria-hidden`; `speech` is what is true.
//  A 40px bar reading "Klaphake" still announces "Thomas Klaphake".
// ─────────────────────────────────────────────
import React from "react";
import { to12h } from "../../domain/timeline";
import S from "./caseBarStyles";

// A handle is 24px wide — WCAG 2.2 SC 2.5.8 Target Size (Minimum, AA).
export const HANDLE_W = 24;

export default function CaseBarShell({
  barId,
  // Percent of the axis span — `barMetrics` output, never pixels.
  left,
  width,
  tint,
  onCall = false,
  title,
  speech,
  canOpen = false,
  onOpen,
  // Gesture state for the CASE window, from `useBarDrag`.
  gesturing = false,
  draggable = false,
  edges = [],
  handleProps,
  focusEdge = null,
  // Handles that are not at an edge of the bar. Today that is exactly one: the
  // second provider's leaving time, which sits at THEIR hatch boundary inside
  // THEIR band. `{ key, props, leftPercent, top, height, focused }`.
  //
  // ⚠️ THEY SIT ABOVE THE CASE HANDLES (zIndex 3 vs 2). A second provider who
  // leaves at the case end puts their handle exactly over the case's end
  // handle; the vertical bands are what disambiguate, and inside their own band
  // the person's own time has to win. The case handle is still 38px of grab
  // height in the other bands.
  extraHandles = [],
  children,
  // The live readout while a gesture is in flight.
  tipMin = null,
  tipLeft = 0,
}) {
  return (
    <>
      <div
        data-tl-bar={barId}
        style={{
          ...S.bar,
          ...(onCall ? S.barOnCall : null),
          left: `${left}%`,
          width: `${width}%`,
          background: tint.bg,
          borderColor: tint.border,
          color: tint.head,
          // `S.barLabel` carries no user-select rule, so without this a drag
          // across the bar text-selects the CRNA's name mid-gesture.
          ...(gesturing ? { userSelect: "none" } : null),
        }}
        // ⚠️ BLANKED WHILE DRAGGING. The OS tooltip pops over the gesture and
        // sits there quoting the time the bar USED to have.
        title={gesturing ? undefined : title}
      >
        {/* ── THE CLICK TARGET ────────────────────────────────────────────
            Transparent, fills the bar, and sits UNDER the names (zIndex 1 vs 3)
            and under the drag handles (zIndex 2). Clicking anywhere that is not
            a name or an edge opens the case modal, which is this chart's WCAG
            2.2 SC 2.5.7 alternative to the drag. */}
        {canOpen ? (
          <div
            data-tl-open=""
            role="button"
            tabIndex={0}
            title={gesturing ? undefined : title}
            style={{ ...S.openTarget, ...(gesturing ? { userSelect: "none" } : null) }}
            onClick={(e) => {
              e.stopPropagation();
              if (onOpen) onOpen();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (onOpen) onOpen({ fromKeyboard: true });
              }
            }}
          >
            <span style={S.srOnly}>{speech}</span>
          </div>
        ) : (
          <span style={S.srOnly}>{speech}</span>
        )}

        {children}

        {/* ⚠️ HANDLES LIVE INSIDE THE BAR. `S.bar` sets `overflow: hidden`, so a
            handle that overhangs an edge is simply not drawn. */}
        {draggable &&
          edges.map((edge) => (
            <span
              key={edge}
              {...handleProps(edge)}
              style={{
                ...S.handle,
                width: HANDLE_W,
                top: 0,
                bottom: 0,
                ...(edge === "start" ? { left: 0 } : { right: 0 }),
                ...(focusEdge === edge ? S.handleFocus : null),
              }}
            />
          ))}
        {extraHandles.map((h) => (
          <span
            key={h.key}
            {...h.props}
            style={{
              ...S.handle,
              width: HANDLE_W,
              zIndex: 3,
              top: h.top,
              height: h.height,
              bottom: "auto",
              // Centred ON the boundary it moves, so the thing you grab is the
              // thing you see.
              left: `calc(${h.leftPercent}% - ${HANDLE_W / 2}px)`,
              ...(h.focused ? S.handleFocus : null),
            }}
          />
        ))}
      </div>

      {/* The live time readout. A SIBLING of the bar, not a child: the bar clips
          its overflow, so a tooltip inside it would be invisible. Above the
          now-line (zIndex 6), and `aria-hidden` because the slider's
          `aria-valuetext` is what a screen reader is already following. */}
      {tipMin != null && (
        <span
          aria-hidden="true"
          style={{
            ...S.tip,
            left: `${tipLeft}%`,
            transform: tipLeft >= 98 ? "translateX(-100%)" : tipLeft <= 2 ? "none" : "translateX(-50%)",
          }}
        >
          {to12h(tipMin)}
        </span>
      )}
    </>
  );
}
